// Canvas helpers for share-image rendering.
//
// Pure utilities only — no state, no DOM beyond `<canvas>` and `<img>`. The
// composition logic (drawShareCanvas, openResultShareSheet) lives in share/.
//
// `wrapTextLines` does character-level wrapping because Korean has no inter-
// word spaces; the western-style word-break would leave most Korean lines
// either far too short or overflowing.

export const SHARE_FONT_BASE =
  'Pretendard, -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", system-ui, "Segoe UI", "Malgun Gothic", "Apple Color Emoji", "Segoe UI Emoji", sans-serif';

export function shareFont(weight: string | number, size: number): string {
  return `${weight} ${size}px ${SHARE_FONT_BASE}`;
}

export function loadImageAsync(url: string | null | undefined): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  ctx.lineTo(x + rr, y + h);
  ctx.arcTo(x, y + h, x, y + h - rr, rr);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.closePath();
}

// Break Korean/English text into lines that fit within maxWidth.
// Uses character-level wrapping (Korean has no spaces between phrases).
export function wrapTextLines(
  ctx: CanvasRenderingContext2D,
  text: string | null | undefined,
  maxWidth: number,
  maxLines?: number,
): string[] {
  const raw = String(text ?? "").replace(/\s+/g, " ").trim();
  if (!raw) return [];
  const chars = [...raw];
  const lines: string[] = [];
  let cur = "";
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    const next = cur + ch;
    if (ctx.measureText(next).width <= maxWidth) {
      cur = next;
      continue;
    }
    // Prefer breaking at a space if present.
    const lastSpace = cur.lastIndexOf(" ");
    if (lastSpace > 0 && cur.length - lastSpace <= 10) {
      lines.push(cur.slice(0, lastSpace));
      cur = cur.slice(lastSpace + 1) + ch;
    } else {
      lines.push(cur);
      cur = ch;
    }
    if (maxLines && lines.length >= maxLines) {
      // Truncate remaining text into current line with ellipsis.
      let tail = cur + chars.slice(i + 1).join("");
      while (tail.length > 0 && ctx.measureText(tail + "…").width > maxWidth) {
        tail = tail.slice(0, -1);
      }
      cur = tail + "…";
      break;
    }
  }
  if (cur) lines.push(cur);
  if (maxLines && lines.length > maxLines) {
    const clipped = lines.slice(0, maxLines);
    let last = clipped[maxLines - 1];
    while (last.length > 0 && ctx.measureText(last + "…").width > maxWidth) {
      last = last.slice(0, -1);
    }
    clipped[maxLines - 1] = last + "…";
    return clipped;
  }
  return lines;
}

export function ellipsizeText(
  ctx: CanvasRenderingContext2D,
  text: string | null | undefined,
  maxWidth: number,
): string {
  const raw = String(text ?? "");
  if (ctx.measureText(raw).width <= maxWidth) return raw;
  let s = raw;
  while (s.length > 0 && ctx.measureText(s + "…").width > maxWidth) {
    s = s.slice(0, -1);
  }
  return s + "…";
}
